'use client';
import React, { FormEvent, useState, ChangeEvent } from 'react';

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => (
  <article className={`rounded-2xl p-6 ${className || ''}`} style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
    {children}
  </article>
);

const Chip: React.FC<React.PropsWithChildren<{ href: string }>> = ({ href, children }) => (
  <a href={href} className="px-3 py-1 rounded-full hover:bg-white/25 text-sm" style={{ background: 'rgba(255,255,255,.14)' }}>
    {children}
  </a>
);

export default function PartnersPage() {
  const [avgFee, setAvgFee] = useState<number | ''>('');
  const [refs, setRefs] = useState<number | ''>('');
  const [pct, setPct] = useState<number>(15);
  const [res, setRes] = useState<string>('');

  const fmt = (n: number) => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(n);

  function onCalc(e: FormEvent) {
    e.preventDefault();
    const a = Number(avgFee || 0);
    const r = Number(refs || 0);
    const p = Number(pct || 0);
    setRes(`Оценка выплат партнёру: ${fmt(a * r * (p / 100))} в месяц (${r} реферала × ${fmt(a)} × ${p}%).`);
  }

  const [partnerForm, setPartnerForm] = useState({
    company: '',
    contactPerson: '',
    email: '',
    website: '',
    partnershipType: 'Тип партнёрства',
    collaborationIdea: '',
  });

  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [showConsentWarning, setShowConsentWarning] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handlePartnerChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPartnerForm({ ...partnerForm, [name]: value });
  };

  const handleConsentChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked) {
      setShowConsentWarning(false);
    }
  };

  const handlePartnerSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setShowConsentWarning(false);

    if (partnerForm.partnershipType === 'Тип партнёрства') {
      setIsSubmitting(false);
      setSubmitStatus('error');
      console.error('Client-side validation failed: Partnership Type missing.');
      return;
    }

    if (!isConsentChecked) {
      setIsSubmitting(false);
      setShowConsentWarning(true);
      return;
    }

    const payload = {
      ...partnerForm,
      formName: "Партнёрства INTERIM", 
      partnershipType: partnerForm.partnershipType === 'Тип партнёрства' ? '' : partnerForm.partnershipType
    };

    try {
      const maxRetries = 3;
      let lastError = null;

      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/partner-applications`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: payload }),
          });

          if (response.ok) {
            setSubmitStatus('success');
            setPartnerForm({
              company: '',
              contactPerson: '',
              email: '',
              website: '',
              partnershipType: 'Тип партнёрства',
              collaborationIdea: '',
            });
            setIsConsentChecked(false);
            return;
          } else {
            if (response.status === 400) {
              const errorData = await response.json();
              console.error('Strapi 400 Bad Request error details:', errorData);
              setSubmitStatus('error');
              return;
            }
            throw new Error(`Server returned status: ${response.status}`);
          }
        } catch (error) {
          lastError = error;
          if (attempt < maxRetries - 1) {
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      console.error('Final error submitting form:', lastError);
      setSubmitStatus('error');

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const customStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');

    .custom-checkbox {
        appearance: none;
        cursor: pointer;
        transition: all 0.15s ease-in-out;
    }

    .custom-checkbox:checked {
        background-color: #FFC300; 
        border-color: #FFC300;     
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23102a43' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
        background-size: 80%; 
        background-repeat: no-repeat;
        background-position: center;
    }
  `;

  return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen font-sans">
      <style dangerouslySetInnerHTML={{ __html: customStyles }} />
      <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
        style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
        <div className="max-w-7xl mx-auto">
          <nav className="mb-6 flex flex-wrap gap-3 text-sm">
            <Chip href="#models">Модели партнёрства</Chip>
            <Chip href="#benefits">Преимущества</Chip>
            <Chip href="#tiers">Тиры</Chip>
            <Chip href="#programs">Совместный маркетинг</Chip>
            <Chip href="#cases">Кейсы</Chip>
            <Chip href="#onboarding">Онбординг</Chip>
            <Chip href="#refcalc">Калькулятор реферала</Chip>
            <Chip href="#faq">FAQ</Chip>
            <Chip href="#apply">Стать партнёром</Chip>
          </nav>
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Партнёрства INTERIM</h1>
          <p className="max-w-3xl text-lg md:text-xl text-gray-200">
            Объединяемся с компаниями, вузами и технологическими вендорами. Прозрачные условия, общий рост и измеримые результаты.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="/cooperation" className="px-4 py-3 rounded-xl font-semibold transition duration-300 transform hover:scale-[1.03] shadow-lg" style={{ background: '#FFC300', color: '#102a43' }}>Сотрудничество</a>
            <a href="#programs" className="px-[15px] py-[11px] rounded-xl font-semibold border border-blue-600 text-blue-600 bg-white transition duration-300 transform hover:shadow-lg">Возможности ко-маркетинга</a>
          </div>
        </div>
      </header>
      <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
        <section id="models" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Модели партнёрства</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: 'Реферальные партнёры',
                desc: 'Вы рекомендуете клиента — мы платим комиссию 10–20% от нашей комиссии.',
                items: ['Договор и прозрачный учёт лидов', 'Период выплат — ежемесячно']
              },
              {
                title: 'Вендоры/субподрядчики (MSP)',
                desc: 'Объединяем пул подрядчиков: единые SLA, отчётность и тарифы.',
                items: ['Единая сетка ставок', 'Оценка качества и рейтинг']
              },
              {
                title: 'Технологические партнёры',
                desc: 'Интеграции с ATS/HRIS/CRM/BI, совместные кейсы и вебинары.',
                items: ['ATS', 'HRIS', 'CRM', 'BI']
              },
              { title: 'Вузы/образование', desc: 'Стажировки, ярмарки вакансий, кадровые резервы.', items: [] },
              { title: 'HR-консалтинг', desc: 'Делаем совместные проекты по аудитам и внедрению процессов.', items: [] },
              { title: 'Агентства-партнёры', desc: 'Входим в MSP-портфель, берём сегменты/регионы.', items: [] }
            ].map((model) => (
              <Card key={model.title}>
                <h3 className="text-xl font-semibold mb-2">{model.title}</h3>
                <p className="text-sm text-gray-200">{model.desc}</p>
                {model.items.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {model.items.map(item => (
                      <span key={item} className="px-2.5 py-1 rounded-full text-xs" style={{ background: 'rgba(255,255,255,.12)' }}>
                        {model.title === 'Технологические партнёры' ? item : `• ${item}`}
                      </span>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>

        <section id="benefits" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Что получает партнёр</h2>
          <div className="grid gap-6 md:grid-cols-4">
            {[
              ['SLA & отчётность', 'Прозрачные метрики, BI-дашборды, ежемесячные обзоры.'],
              ['Ко-брендинг', 'Совместные вебинары/кейсы/выпуски рассылки.'],
              ['Дедикейтед менеджер', 'Единая точка входа и приоритизация запросов.'],
              ['Платежи вовремя', 'Фиксированные условия выплат и акты.']
            ].map(([t, d]) => (
              <Card key={t}><div className="text-2xl font-bold mb-1">{t}</div><p className="text-sm text-gray-200">{d}</p></Card>
            ))}
          </div>
        </section>
        <section id="tiers" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Партнёрские уровни</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['SILVER', 'Стартовый уровень', ['Комиссия 10%', 'Упоминание в каталоге партнёров', 'Доступ к материалам ко-маркетинга'], 'text-white'],
              ['GOLD', 'Расширенные привилегии', ['Комиссия 15%', 'Совместные вебинары и кейсы', 'Приоритизация заявок'], 'text-yellow-300'],
              ['PLATINUM', 'Стратегический партнёр', ['Комиссия 20%', 'Совместные продуктовые эксперименты', 'Дедикейтед аккаунт и roadmap'], 'text-white'],
            ].map(([tier, sub, perks, color]) => (
              <Card key={tier as string}>
                <div className={`${color as string} font-bold text-sm`}>{tier as string}</div>
                <h3 className="text-xl font-semibold mt-1">{sub as string}</h3>
                <ul className="text-sm text-gray-200 mt-3 space-y-2">
                  {(perks as string[]).map(p => <li key={p}>• {p}</li>)}
                </ul>
              </Card>
            ))}
          </div>
        </section>

        <section id="programs" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Совместный маркетинг</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['Вебинары и митапы', 'Общая аудитория и лидогенерация, запись и разбор кейсов.'],
              ['Контент и кейсы', 'Статьи, PDF-гайды, кейсы с цифрами и цитатами.'],
              ['Пакеты для продаж', 'One-pager, презентации, гайд по интеграции.'],
            ].map(([t, d]) => (
              <Card key={t}><h3 className="text-xl font-semibold">{t}</h3><p className="text-sm text-gray-200 mt-1">{d}</p></Card>
            ))}
          </div>
        </section>
        <section id="cases" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Кейсы партнёрств</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              ['RPO + ATS', 'Интеграция с ATS: −19% CPH, рост явки до 96%.'],
              ['MSP-пул агентств', 'Единая сетка ставок, рейтинг качества, экономия 9%.'],
              ['Университетская программа', 'Стажировки, 120+ трудоустройств за 2 семестра.'],
            ].map(([t, d]) => (
              <Card key={t}><h3 className="text-xl font-semibold">{t}</h3><p className="text-sm text-gray-200 mt-1">{d}</p></Card>
            ))}
          </div>
        </section>
        <section id="onboarding" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Как стать партнёром</h2>
          <ol className="grid gap-6 md:grid-cols-3">
            {[
              ['1. Заявка', 'Краткая форма и первичная квалификация.'],
              ['2. Встреча', 'Цели, аудитория, согласование модели.'],
              ['3. Договор', 'MSA/NDA, согласование KPI и процесса.'],
              ['4. Запуск', 'Онбординг, доступы, план первого квартала.'],
            ].map(([t, d]) => (
              <Card key={t} className="list-none"><div className="text-2xl font-bold mb-1">{t}</div><p className="text-sm text-gray-200">{d}</p></Card>
            ))}
          </ol>
        </section>
        <section id="refcalc" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Калькулятор реферальных выплат</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <form onSubmit={onCalc} className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="avgFee" className="block text-sm font-medium text-gray-200 mb-1">Средняя комиссия INTERIM за найм, ₽</label>
                  <input type="number" id="avgFee" min={30000} className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="например, 120 000" value={avgFee} onChange={e => setAvgFee(e.target.value ? Number(e.target.value) : '')} required />
                </div>
                <div>
                  <label htmlFor="refs" className="block text-sm font-medium text-gray-200 mb-1">Количество ваших рефералов в мес.,шт.</label>
                  <input type="number" id="refs" min={1} className="w-full rounded-md px-3 py-2 text-gray-900" placeholder="например, 3" value={refs} onChange={e => setRefs(e.target.value ? Number(e.target.value) : '')} required />
                </div>
              </div>
              <div>
                <label htmlFor="pct" className="block text-sm font-medium text-gray-200 mb-1">Процент партнёра</label>
                <select id="pct" className="w-full rounded-md px-3 py-2 text-gray-900" value={pct} onChange={e => setPct(Number(e.target.value))}>
                  <option value={10}>10%</option>
                  <option value={15}>15%%</option>
                  <option value={20}>20%</option>
                </select>
              </div>
              <button type="submit" className="w-full rounded-xl py-2 font-semibold transition duration-300 transform hover:scale-[1.01]" style={{ background: '#FFC300', color: '#102a43' }}>Рассчитать</button>
            </form>
            <Card>
              <h3 className="text-xl font-semibold mb-2">Результат</h3>
              <div className="text-lg min-h-[2.5rem]">{res || 'Заполните форму, чтобы увидеть расчёт.'}</div>
              <p className="text-xs text-gray-300 mt-4">* Итог зависит от модели, сроков и согласованных KPI.</p>
            </Card>
          </div>
        </section>
        <section id="faq" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
          <div className="space-y-3">
            {[
              ['Когда происходят выплаты рефералам?', 'Раз в месяц, после закрытия актов и фактической оплаты клиентом.'],
              ['Можно ли совмещать модели?', 'Да, например реферальная модель + ко-маркетинг, или MSP + интеграции.'],
              ['Как считаем атрибуцию лида?', 'Фиксируем в CRM источник и партнёра, SLA на обработку лида — 24 часа.'],
            ].map(([q, a]) => (
              <details key={q} className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,.08)' }}>
                <summary className="cursor-pointer font-semibold">{q}</summary>
                <p className="mt-2 text-sm text-gray-200">{a}</p>
              </details>
            ))}
          </div>
        </section>
        <section id="apply" className="scroll-mt-32">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Стать партнёром INTERIM</h2>
          <form
            onSubmit={handlePartnerSubmit}
            className="rounded-2xl p-6 grid gap-4 md:grid-cols-2"
            style={{ background: 'rgba(255,255,255,.08)', backdropFilter: 'blur(8px)' }}
          >
            <input
              name="company"
              value={partnerForm.company}
              onChange={handlePartnerChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Компания*"
              required
            />
            <input
              name="contactPerson"
              value={partnerForm.contactPerson}
              onChange={handlePartnerChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Контактное лицо*"
              required
            />
            <input
              name="email"
              value={partnerForm.email}
              onChange={handlePartnerChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="E-mail*"
              type="email"
              required
            />
            <input
              name="website"
              value={partnerForm.website}
              onChange={handlePartnerChange}
              className="rounded-xl px-4 py-3 text-gray-900"
              placeholder="Сайт/ссылка"
            />
            <select
              name="partnershipType"
              value={partnerForm.partnershipType}
              onChange={handlePartnerChange}
              className={`rounded-xl px-4 py-3 ${partnerForm.partnershipType === 'Тип партнёрства' ? 'text-gray-500' : 'text-gray-900'}`}
              required
            >
              <option disabled value="Тип партнёрства" hidden>Тип партнёрства*</option>
              <option>Реферальный партнёр</option>
              <option>MSP/субподрядчик</option>
              <option>Технологический партнёр</option>
              <option>Вуз/образование</option>
              <option>HR-консалтинг</option>
            </select>
            <textarea
              name="collaborationIdea"
              value={partnerForm.collaborationIdea}
              onChange={handlePartnerChange}
              className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
              rows={4}
              placeholder="Опишите аудиторию/идею сотрудничества"
            ></textarea>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent"
                  checked={isConsentChecked}
                  onChange={handleConsentChange}
                  className="w-5 h-5 rounded border-2 border-gray-400 custom-checkbox appearance-none"
                />
                <span className="text-sm text-gray-200">
                  Я соглашаюсь с <a href="#" className="underline hover:text-white">обработкой персональных данных</a>*
                </span>
              </label>
            </div>

            {showConsentWarning && (
              <div className="md:col-span-2 p-3 rounded-xl text-center font-medium  border-yellow-400 text-цwhite transition-all duration-300">
                Пожалуйста, дайте согласие на обработку персональных данных, чтобы отправить заявку.
                <button
                  onClick={() => setShowConsentWarning(false)}
                  className="ml-3 font-semibold text-yellow-100 hover:text-white underline"
                >
                  Скрыть
                </button>
              </div>
            )}

            <button
              type="submit"
              className="rounded-xl px-4 py-3 font-semibold md:col-span-2 transition duration-300 transform hover:scale-[1.01]"
              style={{ background: '#FFC300', color: '#102a43' }}
              disabled={isSubmitting || partnerForm.partnershipType === 'Тип партнёрства'}
            >
              {isSubmitting ? 'Отправка...' : 'Отправить'}
            </button>
            {submitStatus === 'success' && (
              <div className="md:col-span-2 text-center text-sm text-green-300">
                ✅ Ваша заявка успешно отправлена!
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="md:col-span-2 text-center text-sm text-red-400">
                ❌ Ошибка при отправке. Проверьте консоль для деталей или настройки Strapi.
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
}
